import { Transaction, doc } from 'firebase/firestore'
import { QUOTA_MESSAGE, db, isQuotaError } from '../store/firebase'
import { User } from '../types'
import { BillingAuditEntry, BillingConfig, NumberSeries } from '../types/billing'
import { makeId } from './billingMoney'

/** Billing v2 collections. Legacy collections (invoices, payments, …) are left untouched as the backup. */
export const BCOL = {
  config: 'billing_config',
  accounts: 'billing_accounts',
  items: 'billing_items',
  stockMoves: 'billing_stock_moves',
  documents: 'billing_documents',
  payments: 'billing_payments',
  sales: 'billing_direct_sales',
  creditNotes: 'billing_credit_notes',
  refunds: 'billing_refunds',
  recurring: 'billing_recurring',
  claims: 'billing_charge_claims',
  numbers: 'billing_numbers',
  audit: 'billing_audit',
  counters: 'counters',
}
export const CONFIG_ID = 'main'

export const clean = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T

export const failure = (error: unknown, fallback: string) => {
  console.error(fallback, error)
  if (isQuotaError(error)) return { ok: false as const, error: QUOTA_MESSAGE }
  return { ok: false as const, error: error instanceof Error && error.message ? error.message : fallback }
}

/** Yearly series keep one counter per Lusaka year, so the sequence restarts each 1 January. */
export const counterRef = (series: NumberSeries, year?: string) =>
  doc(db, BCOL.counters, year ? `billing-${series}-${year}` : `billing-${series}`)
export const numberRef = (series: NumberSeries, number: string) =>
  doc(db, BCOL.numbers, `${series}__${number.replace(/[/\\#?]/g, '_')}`)

const lusakaYear = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Lusaka', year: 'numeric' }).format(new Date())

export function formatNumber(config: BillingConfig, series: NumberSeries, n: number, year = lusakaYear()) {
  const rule = config.numbering[series]
  return `${rule.prefix}${rule.yearly ? `${year}-` : ''}${String(n).padStart(rule.padding, '0')}`
}

/** Reads the last-used sequence value for a series (current year for yearly series). */
export async function readCounter(tx: Transaction, config: BillingConfig, series: NumberSeries) {
  const year = config.numbering[series].yearly ? lusakaYear() : undefined
  const snap = await tx.get(counterRef(series, year))
  return { ref: counterRef(series, year), last: Number(snap.data()?.last) || 0 }
}

/**
 * Reserves the next document number inside a transaction. The counter is read
 * first (all reads must precede writes), and the number itself is reserved as a
 * document, so concurrent users can never receive the same number and numbers
 * imported from the old system can never be reissued.
 */
export type NumberPlan = { series: NumberSeries; numbers: string[]; last: number; counter: ReturnType<typeof counterRef> }

/** READ phase — call before any transaction writes. Skips numbers already reserved (e.g. migrated history). */
export async function planNumbers(
  tx: Transaction,
  config: BillingConfig,
  series: NumberSeries,
  count: number,
): Promise<NumberPlan> {
  const counter = await readCounter(tx, config, series)
  let last = counter.last
  const numbers: string[] = []
  let guard = 0
  while (numbers.length < count) {
    guard += 1
    if (guard > 200) throw new Error(`Could not find a free ${series} number. Check Billing Settings → Document numbering.`)
    last += 1
    const candidate = formatNumber(config, series, last)
    const taken = await tx.get(numberRef(series, candidate))
    if (!taken.exists()) numbers.push(candidate)
  }
  return { series, numbers, last, counter: counter.ref }
}

/** WRITE phase — reserves the planned numbers and advances the counter. */
export function commitNumbers(tx: Transaction, plan: NumberPlan, ownerIds: string[]) {
  const at = new Date().toISOString()
  tx.set(plan.counter, { last: plan.last, updatedAt: at })
  plan.numbers.forEach((number, i) =>
    tx.set(numberRef(plan.series, number), { ownerId: ownerIds[i] ?? ownerIds[0], series: plan.series, number, at }),
  )
}

export function writeAudit(
  tx: Transaction,
  user: User,
  entry: Omit<BillingAuditEntry, 'id' | 'at' | 'userId' | 'userName'>,
) {
  const log: BillingAuditEntry = clean({ ...entry, id: makeId('bau'), at: new Date().toISOString(), userId: user.id, userName: user.fullName })
  tx.set(doc(db, BCOL.audit, log.id), log)
}
